import {DatePipe} from '@angular/common';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '@/environments/environment';

import {Equipment} from '@models/Equipment';
import {FindedPerson, Person, SearchedPerson} from '@models/Person';
import {CheckupProtocol} from '@models/CheckupProtocol';
import {SurveyIndicator} from '@models/SurveyIndicator';
import {TypeOfExamination} from '@models/TypeOfExamination';
import {TypeOfExaminationDTO} from '@models/TypeOfExaminationDTO';
import {Programms} from '@models/Programms';
import {Conditions} from '@models/Conditions';
import {ExaminationAge} from '@models/ExaminationAge';
import {PredispositionAge} from '@models/PredispositionAge';
import {GroupOfSurveyType} from '@models/GroupOfSurveyType';
import {Archive} from '@models/Archive';
import {CommonList} from '@models/CommonList';
import {BaseFilter} from '@models/BaseFilter';
import {PageFilter} from '@models/PageFilter';
import {SortFilter} from '@models/SortFilter';
import {PersonFilter} from '@models/PersonFilter';
import {ProtocolFilter} from '@models/ProtocolFilter';
import {NameAndDateRangeFilter} from '@models/NameAndDateRangeFilter';
import {CheckupProtocolSportReportBA} from '@models/CheckupProtocolSportReportBA';
import {CheckupProtocolSport} from '@models/CheckupProtocolSport';
import {ServerResult} from '@models/ServerResult';
import {NormData, RekData} from '@models/RekData';
import {RekListData} from '@models/RekListData';
import {PoksData} from '@models/PoksData';
import {ProtocolDate} from '@models/ProtocolDate';
import {Reference} from '@models/Reference';
import {GroupDevelopQualitiesTree} from '@models/GroupDevelopQualitiesTree';
import {MeasureDto} from '@models/MeasureDto';
import {MeasureUnit} from '@models/MeasureUnit';
import {Poks} from '@models/Poks';
import {AddProtoModel, RepsonUpdateResult, SportNormatives,} from '@models/valueDto';
import {NormsFileData} from '@models/NormsFileData';
import {ControlExerciseValueDto} from '@models/ControlExerciseValueDto';
import {CreateControlExerciseValueDto} from '@models/CreateControlExerciseValueDto';
import {UpdateControlExerciseValueDto} from '@models/UpdateControlExerciseValueDto';

@Injectable()
export class ApiClient {
    private url: string = '';
    private prefix: string = '/api';

    private AddQueryParamsByBaseFilter(filter: BaseFilter): HttpParams {
        let queryParams = new HttpParams();
        if (filter == null) return queryParams;

        if (filter.isArchive) {
            queryParams = queryParams.append('isArchive', filter.isArchive);
        }

        return queryParams;
    }

    private AddQueryParamsByPageFilter(filter: PageFilter): HttpParams {
        let queryParams = this.AddQueryParamsByBaseFilter({ ...filter });
        if (filter == null) return new HttpParams();

        queryParams = queryParams.append('pageIndex', filter.pageIndex);
        queryParams = queryParams.append('pageSize', filter.pageSize);

        return queryParams;
    }

    private AddQueryParamsBySortFilter(filter: SortFilter): HttpParams {
        let queryParams = this.AddQueryParamsByPageFilter({ ...filter });
        if (filter == null) return new HttpParams();

        if (filter.sort) {
            filter.sort.forEach((item, index) => {
                queryParams = queryParams.append(
                    `sort[${index}].columnName`,
                    item.columnName
                );
                queryParams = queryParams.append(
                    `sort[${index}].ascending`,
                    item.ascending
                );
            });
        }

        return queryParams;
    }

    private AddQueryParamsByNameAndDateRangeFilter(
        filter: NameAndDateRangeFilter
    ): HttpParams {
        let queryParams = this.AddQueryParamsBySortFilter({ ...filter });
        if (filter == null) return new HttpParams();

        const datePipe = new DatePipe('ru-RU');

        if (filter.fname) {
            queryParams = queryParams.append('fname', filter.fname);
        }

        if (filter.sname) {
            queryParams = queryParams.append('sname', filter.sname);
        }

        if (filter.tname) {
            queryParams = queryParams.append('tname', filter.tname);
        }

        if (filter.dateRange?.dateMin) {
            const dateMin = datePipe.transform(
                filter.dateRange.dateMin,
                'yyyy-MM-dd',
                'UTC +0'
            );
            queryParams = queryParams.append('dateRange.dateMin', `${dateMin}`);
        }

        if (filter.dateRange?.dateMax) {
            const dateMax = datePipe.transform(
                filter.dateRange.dateMax,
                'yyyy-MM-dd',
                'UTC +0'
            );
            queryParams = queryParams.append('dateRange.dateMax', `${dateMax}`);
        }

        return queryParams;
    }

    private AddQueryParamsByCheckupProtocolSportFilter(
        filter: ProtocolFilter
    ): HttpParams {
        const datePipe = new DatePipe('ru-RU');

        let queryParams = this.AddQueryParamsByNameAndDateRangeFilter({
            ...filter,
        });
        if (filter == null) return new HttpParams();

        if (filter.protocolIds?.length > 0) {
            filter.protocolIds.forEach((item, index) => {
                queryParams = queryParams.append(`protocolIds[${index}]`, item);
            });
        }

        if (filter.statuses?.length > 0) {
            filter.statuses.forEach((item, index) => {
                queryParams = queryParams.append(`statuses[${index}]`, item);
            });
        }

        if (filter.groupIds?.length > 0) {
            filter.groupIds.forEach((item, index) => {
                queryParams = queryParams.append(`groupIds[${index}]`, item);
            });
        }

        if (filter.kindIds?.length > 0) {
            filter.kindIds.forEach((item, index) => {
                queryParams = queryParams.append(`kindIds[${index}]`, item);
            });
        }

        if (filter.sportIds?.length > 0) {
            filter.sportIds.forEach((item, index) => {
                queryParams = queryParams.append(`sportIds[${index}]`, item);
            });
        }

        if (filter.personIds?.length > 0) {
            filter.personIds.forEach((item, index) => {
                queryParams = queryParams.append(`personIds[${index}]`, item);
            });
        }

        if (filter.date) {
            const date = datePipe.transform(
                filter.date,
                'yyyy-MM-dd',
                'UTC +0'
            );
            queryParams = queryParams.append('date', `${date}`);
        }

        return queryParams;
    }

    private AddQueryParamsByPersonFilter(filter: PersonFilter): HttpParams {
        let queryParams = this.AddQueryParamsByNameAndDateRangeFilter({
            ...filter,
        });
        if (filter == null) return queryParams;

        if (filter.sex) {
            queryParams = queryParams.append('sex', filter.sex);
        }

        if (filter.age?.ageMin) {
            queryParams = queryParams.append('age.ageMin', filter.age.ageMin);
        }

        if (filter.age?.ageMax) {
            queryParams = queryParams.append('age.ageMax', filter.age.ageMax);
        }

        if (filter.typeIds?.length > 0) {
            filter.typeIds.forEach((item, index) => {
                queryParams = queryParams.append(`typeIds[${index}]`, item);
            });
        }

        if (filter.qualificationIds?.length > 0) {
            filter.qualificationIds.forEach((item, index) => {
                queryParams = queryParams.append(
                    `qualificationIds[${index}]`,
                    item
                );
            });
        }

        if (filter.stageIds?.length > 0) {
            filter.stageIds.forEach((item, index) => {
                queryParams = queryParams.append(`stageIds[${index}]`, item);
            });
        }

        if (filter.sportIds?.length > 0) {
            filter.sportIds.forEach((item, index) => {
                queryParams = queryParams.append(`sportIds[${index}]`, item);
            });
        }

        return queryParams;
    }

    constructor(private http: HttpClient) {
        this.url = `${environment.schema}://${environment.apiUrl}${this.prefix}`;
    }

    public getVersion(): Observable<any> {
        return this.http.get(`${this.url}/system/version`, {
            responseType: 'text',
        });
    }

    public getPackages(): Observable<any> {
        return this.http.get(`${this.url}/system/packages`, {
            responseType: 'text',
        });
    }

    public parseVisualFormula(formula: string): Observable<any> {
        return this.http.post(`${this.url}/parseVisualFormula`, {
            formula: formula,
        });
    }

    // -------------------------------- EXTERNAL PERSON ---------------------------------------------------------

    public searchExternalPerson(
        personFilter: any
    ): Observable<ServerResult<FindedPerson[]>> {
        return this.http.get<ServerResult<FindedPerson[]>>(
            `${this.url}/external/clients`,
            { params: personFilter }
        );
    }

    public createPerson(
        personId: number
    ): Observable<ServerResult<SearchedPerson>> {
        return this.http.get<ServerResult<SearchedPerson>>(
            `${this.url}/external/clients/import?personId=${personId}`
        );
    }

    public personUpdate(
        personId: number
    ): Observable<ServerResult<RepsonUpdateResult>> {
        return this.http.get<ServerResult<RepsonUpdateResult>>(
            `${this.url}/external/clients/update?personId=${personId}`
        );
    }

    // -------------------------------- MEASURES    -------------------------------------------------------------

    public getMeasureList(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/measure`,
            { params: queryParams }
        );
    }

    public getSerachMeasureList(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/measures/search`,
            { params: queryParams }
        );
    }

    public getMeasureById(
        measureId: number
    ): Observable<ServerResult<MeasureDto>> {
        return this.http.get<ServerResult<MeasureDto>>(
            `${this.url}/measure/${measureId}`
        );
    }

    public addToArchiveMeasure(
        measureId: number,
        archive: Archive
    ): Observable<ServerResult<MeasureDto>> {
        return this.http.put<ServerResult<MeasureDto>>(
            `${this.url}/measure/archive/${measureId}`,
            archive
        );
    }

    public extractFromArchiveMeasure(
        measureId: number,
        archive: Archive
    ): Observable<ServerResult<MeasureDto>> {
        return this.http.put<ServerResult<MeasureDto>>(
            `${this.url}/measure/archive/${measureId}`,
            archive
        );
    }

    public addMeasure(
        measure: CommonList
    ): Observable<ServerResult<MeasureDto>> {
        return this.http.post<ServerResult<MeasureDto>>(
            `${this.url}/measure`,
            measure
        );
    }

    public updateMeasure(
        measure: CommonList
    ): Observable<ServerResult<MeasureDto>> {
        return this.http.put<ServerResult<MeasureDto>>(
            `${this.url}/measure/${measure.id}`,
            measure
        );
    }

    public addMeasureUnit(
        measureUnit: MeasureUnit,
        measureId: number
    ): Observable<ServerResult<MeasureUnit>> {
        return this.http.post<ServerResult<MeasureUnit>>(
            `${this.url}/measure/${measureId}/measureUnits`,
            measureUnit
        );
    }

    public updateMeasureUnit(
        measureUnit: MeasureUnit,
        measureId: number
    ): Observable<ServerResult<MeasureUnit>> {
        return this.http.put<ServerResult<MeasureUnit>>(
            `${this.url}/measure/${measureId}/measureUnits`,
            measureUnit
        );
    }

    public deleteMeasureUnit(measureUnitId: number) {
        return this.http.delete<ServerResult<MeasureDto>>(
            `${this.url}/measure/measureUnits/${measureUnitId}`
        );
    }

    // -------------------------------- UPLOAD      -------------------------------------------------------------

    public uploadFile(formData: FormData): any {
        return this.http.post(`${this.url}/files/upload`, formData);
    }

    // -------------------------------- СПРАВОЧНИКИ -------------------------------------------------------------

    public getSportByPhysicalQualityId(
        physicalQualityId: number,
        excludeSports: number[] | null = null
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append(
            'physicalQualityId',
            physicalQualityId
        );

        if (excludeSports?.length > 0) {
            excludeSports.forEach((item, index) => {
                queryParams = queryParams.append(
                    `excludeSports[${index}]`,
                    item
                );
            });
        }

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/getSportByPhysicalQualityId`,
            { params: queryParams }
        );
    }

    public getSuggestionsDisciplineGroup(
        physicalQualityId: number,
        excludeDis: number[] | null = null
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append(
            'physicalQualityId',
            physicalQualityId
        );

        if (excludeDis?.length > 0) {
            excludeDis.forEach((item, index) => {
                queryParams = queryParams.append(`excludeDis[${index}]`, item);
            });
        }

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/suggestionsDisciplineGroup`,
            { params: queryParams }
        );
    }

    public findCheckupSport(
        sectionId: number
    ): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/findcheckupsport?sectionId=${sectionId}`
        );
    }

    public getDisciplineGroups(
        sportIds: number[] | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();

        if (sportIds?.length > 0) {
            sportIds.forEach((item, index) => {
                queryParams = queryParams.append(`sportIds[${index}]`, item);
            });
        }

        return sportIds?.length > 0
            ? this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/disciplineGroups`,
                  { params: queryParams }
              )
            : this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/disciplineGroups`
              );
    }

    public getControlExercises(
        sportId: number | undefined = undefined,
        excludeCe: number[] | undefined = undefined,
        isSport: boolean = true
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append('isSport', isSport);

        if (sportId) queryParams = queryParams.append('sportId', sportId);

        if (excludeCe?.length > 0) {
            excludeCe.forEach((item, index) => {
                queryParams = queryParams.append(`excludeCe[${index}]`, item);
            });
        }

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/controlExercises`,
            { params: queryParams }
        );
    }

    public getMeasureUnits(
        measureId: number | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        return measureId
            ? this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/measureUnits?measureId=${measureId}`
              )
            : this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/measureUnits`
              );
    }

    public getAmpluas(
        sportIds: number[] | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();

        if (sportIds?.length > 0) {
            sportIds.forEach((item, index) => {
                queryParams = queryParams.append(`sportIds[${index}]`, item);
            });
        }

        return sportIds?.length > 0
            ? this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/ampluas`,
                  { params: queryParams }
              )
            : this.http.get<ServerResult<Reference[]>>(
                  `${this.url}/references/ampluas`
              );
    }

    public getSportGroups(): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/sportGroups`
        );
    }

    public getMeasures(): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/measures`
        );
    }

    public getTypes(): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/types`
        );
    }

    public getSports(
        sportsmanId: number | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        if (sportsmanId)
            queryParams = queryParams.append('sportsmanId', sportsmanId);

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/sports`,
            { params: queryParams }
        );
    }

    public getQualifications(): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/qualifications`
        );
    }

    public getStages(): Observable<ServerResult<Reference[]>> {
        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/stages`
        );
    }

    public getGroupQualities(
        sportsmanId: number | undefined = undefined,
        sportId: number | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        if (sportsmanId)
            queryParams = queryParams.append('sportsmanId', sportsmanId);
        if (sportId) queryParams = queryParams.append('sportId', sportId);

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/groupqualities`,
            { params: queryParams }
        );
    }

    public getPhysicalQualities(
        sportsmanId: number | undefined = undefined,
        sportId: number | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        if (sportsmanId)
            queryParams = queryParams.append('sportsmanId', sportsmanId);
        if (sportId) queryParams = queryParams.append('sportId', sportId);

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/physicalqualities`,
            { params: queryParams }
        );
    }

    public getProtocolsSport(
        sportsmanId: number | undefined = undefined,
        sportId: number | undefined = undefined
    ): Observable<ServerResult<Reference[]>> {
        let queryParams = new HttpParams();
        if (sportsmanId)
            queryParams = queryParams.append('sportsmanId', sportsmanId);
        if (sportId) queryParams = queryParams.append('sportId', sportId);

        return this.http.get<ServerResult<Reference[]>>(
            `${this.url}/references/checkupprotocols`,
            { params: queryParams }
        );
    }

    // -------------------------------- ОБОРУДОВАНИЕ ------------------------------------------------------------

    public getEquipments(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/equipments`,
            { params: queryParams }
        );
    }

    public updateEquipmentCheckupProtocols(
        equipmentId: number,
        checkupProtocols: number[]
    ): Observable<ServerResult<Equipment>> {
        return this.http.put<ServerResult<Equipment>>(
            `${this.url}/equipments/${equipmentId}/checkupProtocols`,
            checkupProtocols
        );
    }

    public updateEquipmentTypesOfExamination(
        equipmentId: number,
        typesOfExamination: number[]
    ): Observable<ServerResult<Equipment>> {
        return this.http.put<ServerResult<Equipment>>(
            `${this.url}/equipment/${equipmentId}/typesOfExamination`,
            typesOfExamination
        );
    }

    public updateEquipment(
        equipment: CommonList
    ): Observable<ServerResult<Equipment>> {
        return this.http.put<ServerResult<Equipment>>(
            `${this.url}/equipment/${equipment.id}`,
            equipment
        );
    }

    public addEquipment(
        equipment: CommonList
    ): Observable<ServerResult<CommonList[]>> {
        return this.http.post<ServerResult<CommonList[]>>(
            `${this.url}/equipment`,
            equipment
        );
    }

    public archiveEquipment(
        equipmentId: number,
        archive: Archive
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/equipment/archive/${equipmentId}`,
            archive
        );
    }

    public getSerachEquipments(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/equipments/search`,
            { params: queryParams }
        );
    }

    public getEquipment(
        equipmentId: number
    ): Observable<ServerResult<Equipment>> {
        return this.http.get<ServerResult<Equipment>>(
            `${this.url}/equipment/${equipmentId}`
        );
    }

    // -------------------------------- ВМДЫ ОБСЛЕДОВАНИЯ -------------------------------------------------------

    public getSurveyIndicators(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/surveyIndicators`,
            { params: queryParams }
        );
    }

    public updateSurveyIndicator(
        surveyIndicator: CommonList
    ): Observable<ServerResult<SurveyIndicator>> {
        return this.http.put<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicator.id}`,
            surveyIndicator
        );
    }

    public addSurveyIndicator(
        surveyIndicator: CommonList
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/surveyIndicator`,
            surveyIndicator
        );
    }

    public archiveSurveyIndicator(
        surveyIndicatorId: number,
        archive: Archive
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/surveyIndicator/archive/${surveyIndicatorId}`,
            archive
        );
    }

    public getSerachSurveyIndicators(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/surveyIndicators/search`,
            { params: queryParams }
        );
    }

    public getSurveyIndicator(
        surveyIndicatorId: number
    ): Observable<ServerResult<SurveyIndicator>> {
        return this.http.get<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}`
        );
    }

    public updateSurveyIndicatorCheckupProtocols(
        surveyIndicatorId: number,
        checkupProtocols: number[]
    ): Observable<ServerResult<SurveyIndicator>> {
        return this.http.put<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}/checkupProtocols`,
            checkupProtocols
        );
    }

    public updateSurveyIndicatorGroupOfSurveyTypeForTesting(
        surveyIndicatorId: number,
        groupOfSurveyTypeForTesting: number[]
    ): Observable<ServerResult<SurveyIndicator>> {
        return this.http.put<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}/groupOfSurveyTypeForTesting`,
            groupOfSurveyTypeForTesting
        );
    }

    public saveSportForRasp(
        surveyIndicatorId: number,
        sportId: number,
        controlExersiseIds: number[]
    ) {
        return this.http.put<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}/saveSportForRasp`,
            { sportId: sportId, controlExersiseIds: controlExersiseIds }
        );
    }

    public deleteSportForRasp(surveyIndicatorId: number, data: any) {
        if (data.controlExerciseSportId) {
            return this.http.delete<ServerResult<SurveyIndicator>>(
                `${this.url}/surveyIndicator/${surveyIndicatorId}/controlExerciseSportId/${data.controlExerciseSportId}`
            );
        } else {
            return this.http.delete<ServerResult<SurveyIndicator>>(
                `${this.url}/surveyIndicator/${surveyIndicatorId}/controlExerciseDisciplineGroupId/${data.controlExerciseDisciplineGroupId}`
            );
        }
    }

    public saveSportPoints(surveyIndicatorId: number, data: any) {
        return this.http.post<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}/saveSportPoints`,
            data
        );
    }

    public addPhysicalQualityDisciplineGroup(
        surveyIndicatorId: number,
        data: any
    ) {
        return this.http.post<ServerResult<SurveyIndicator>>(
            `${this.url}/surveyIndicator/${surveyIndicatorId}/addPhysicalQualityDisciplineGroup`,
            data
        );
    }

    // -------------------------------- ПОКАЗАТЕЛИ ОБСЛЕДОВАНИЯ -------------------------------------------------

    public changeNormativeType(
        ce: number,
        data: any
    ): Observable<ServerResult<{ ces: number; normType: number }>> {
        return this.http.post<ServerResult<{ ces: number; normType: number }>>(
            `${this.url}/typeOfExamination/${ce}/changeNormativeType`,
            data
        );
    }

    public saveNormativePoints(
        ce: number,
        data: { normType: any; ces: number; points: number }
    ): Observable<ServerResult<{ ces: number; points: number }>> {
        return this.http.post<ServerResult<{ ces: number; points: number }>>(
            `${this.url}/typeOfExamination/${ce}/points`,
            data
        );
    }

    public deleteSportNormativeDataTable(
        ce: number,
        ces: number,
        sportGroupId: undefined | number = undefined
    ): Observable<ServerResult<SportNormatives[]>> {
        if (sportGroupId) {
            return this.http.delete<ServerResult<SportNormatives[]>>(
                `${this.url}/typeOfExamination/${ce}/biomedicalSportTable/${ces}?sportGroupId=${sportGroupId}`
            );
        } else {
            return this.http.delete<ServerResult<SportNormatives[]>>(
                `${this.url}/typeOfExamination/${ce}/biomedicalSportTable/${ces}`
            );
        }
    }

    public updateSportNormativeDataTable(
        ce: number,
        ces: number,
        poksData: PoksData
    ): Observable<ServerResult<Poks[]>> {
        return this.http.put<ServerResult<Poks[]>>(
            `${this.url}/typeOfExamination/${ce}/biomedicalSportTable/${ces}`,
            poksData
        );
    }

    public getSportNormativeDataTable(
        ce: number,
        ces: number,
        normType: number
    ): Observable<ServerResult<Poks[]>> {
        return this.http.get<ServerResult<Poks[]>>(
            `${this.url}/typeOfExamination/${ce}/sportNormativesData/${ces}/${normType}`
        );
    }

    public addDescription(
        typeOfExaminationId: number,
        description: { youtube: string; message: string }
    ): Observable<ServerResult<{ youtube: string; message: string }>> {
        return this.http.put<
            ServerResult<{ youtube: string; message: string }>
        >(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/description`,
            description
        );
    }

    public addSportNormative(
        typeOfExaminationId: number,
        normData: NormData
    ): Observable<ServerResult<boolean>> {
        return this.http.put<ServerResult<boolean>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/normData`,
            normData
        );
    }

    public clearNormativeTable(
        typeOfExaminationId: number,
        normType: number
    ): Observable<ServerResult<boolean>> {
        return this.http.get<ServerResult<boolean>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/normatives/${normType}/clear`
        );
    }

    public getSportNormativeTable(
        typeOfExaminationId: number
    ): Observable<ServerResult<SportNormatives[]>> {
        return this.http.get<ServerResult<SportNormatives[]>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/sportNormatives`
        );
    }

    public getNormativeTable(
        typeOfExaminationId: number
    ): Observable<
        ServerResult<{
            mormType: number;
            dynamicPoks: Poks[];
            rangePoks: Poks[];
        }>
    > {
        return this.http.get<
            ServerResult<{
                mormType: number;
                dynamicPoks: Poks[];
                rangePoks: Poks[];
            }>
        >(`${this.url}/typeOfExamination/${typeOfExaminationId}/normatives`);
    }

    public deleteRecomendation(
        typeOfExaminationId: number,
        reccomendationId: number
    ): Observable<ServerResult<RekListData[]>> {
        return this.http.delete<ServerResult<RekListData[]>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/recommendation/${reccomendationId}`
        );
    }

    public getRecomendation(
        typeOfExaminationId: number,
        reccomendationId: number
    ): Observable<ServerResult<RekData>> {
        return this.http.get<ServerResult<RekData>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/recommendation/${reccomendationId}`
        );
    }

    public getRecomendations(
        typeOfExaminationId: number
    ): Observable<ServerResult<RekListData[]>> {
        return this.http.get<ServerResult<RekListData[]>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/recommendations`
        );
    }

    public addRecomendation(
        typeOfExaminationId: number,
        rekData: RekData
    ): Observable<ServerResult<RekListData[]>> {
        return this.http.post<ServerResult<RekListData[]>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/recommendation`,
            rekData
        );
    }

    public updateRecomendation(
        typeOfExaminationId: number,
        reccomendationId: number,
        rekData: RekData
    ): Observable<ServerResult<RekListData[]>> {
        return this.http.put<ServerResult<RekListData[]>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/recommendation/${reccomendationId}`,
            rekData
        );
    }

    public updateBiomedicalTable(
        typeOfExaminationId: number,
        poksData: PoksData
    ): Observable<ServerResult<PoksData>> {
        return this.http.put<ServerResult<PoksData>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/biomedicalTable`,
            poksData
        );
    }

    public importNormsFileData(
        normsFileData: FormData,
        ce: number,
        ces: number,
        normMode: number
    ): Observable<ServerResult<FormData>> {
        return this.http.put<ServerResult<FormData>>(
            `${this.url}/typeOfExamination/${ce}/typeOfExaminationsport/${ces}/importnorms/${normMode}`,
            normsFileData
        );
    }

    public getTypesOfExamination(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/typesOfExamination`,
            { params: queryParams }
        );
    }

    public getCheckupProtocolTypeOfExamination(
        model: any
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (model.checkUpProtocolSectionId)
            queryParams = queryParams.append(
                'checkUpProtocolSectionId',
                model.checkUpProtocolSectionId
            );
        if (model.checkUpEquipmentId)
            queryParams = queryParams.append(
                'checkUpEquipmentId',
                model.checkUpEquipmentId
            );

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/checkupProtocol/${model.checkUpProtocolId}/typeOfExamination`,
            { params: queryParams }
        );
    }

    public updateTypeOfExaminationPredispositionAge(
        typeOfExaminationId: number,
        predispositionAge: PredispositionAge
    ): Observable<ServerResult<PredispositionAge>> {
        return this.http.put<ServerResult<PredispositionAge>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/predispositionAge`,
            predispositionAge
        );
    }

    public updateTypeOfExaminationExaminationAge(
        typeOfExaminationId: number,
        examinationAge: ExaminationAge
    ): Observable<ServerResult<ExaminationAge>> {
        return this.http.put<ServerResult<ExaminationAge>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/examinationAge`,
            examinationAge
        );
    }

    public updateTypeOfExaminationConditions(
        typeOfExaminationId: number,
        conditions: Conditions
    ): Observable<ServerResult<Conditions>> {
        return this.http.put<ServerResult<Conditions>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/conditions`,
            conditions
        );
    }

    public updateTypeOfExaminationProgramms(
        typeOfExaminationId: number,
        programms: Programms
    ): Observable<ServerResult<Programms>> {
        return this.http.put<ServerResult<Programms>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/programms`,
            programms
        );
    }

    public updateTypeOfExaminationEquipments(
        typeOfExaminationId: number,
        equipments: number[]
    ): Observable<ServerResult<{ [key: number]: string }>> {
        return this.http.put<ServerResult<{ [key: number]: string }>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}/equipments`,
            equipments
        );
    }

    public updateTypeOfExamination(
        typeOfExaminationId: number,
        typeOfExamination: TypeOfExaminationDTO
    ): Observable<ServerResult<TypeOfExamination>> {
        return this.http.put<ServerResult<TypeOfExamination>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}`,
            typeOfExamination
        );
    }

    public addTypeOfExamination(
        typeOfExamination: TypeOfExaminationDTO
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/typeOfExamination`,
            typeOfExamination
        );
    }

    public archiveTypeOfExamination(
        typeOfExaminationId: number,
        archive: Archive
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/typeOfExamination/archive/${typeOfExaminationId}`,
            archive
        );
    }

    public getSerachTypesOfExamination(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/typesOfExamination/search`,
            { params: queryParams }
        );
    }

    public getTypeOfExamination(
        typeOfExaminationId: number
    ): Observable<ServerResult<TypeOfExamination>> {
        return this.http.get<ServerResult<TypeOfExamination>>(
            `${this.url}/typeOfExamination/${typeOfExaminationId}`
        );
    }

    // -------------------------------- ГРУППЫ ВИДОВ ОБСЛЕДОВАНИЯ -----------------------------------------------

    public getGroupOfSurveyTypes(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/groupOfSurveyTypes`,
            { params: queryParams }
        );
    }

    public updateGroupOfSurveyType(
        groupOfSurveyType: CommonList
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.put<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyType.id}`,
            groupOfSurveyType
        );
    }

    public addGroupOfSurveyType(
        groupOfSurveyType: CommonList
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/groupOfSurveyType`,
            groupOfSurveyType
        );
    }

    public archiveGroupOfSurveyType(
        groupOfSurveyTypeId: number,
        archive: Archive
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/groupOfSurveyType/archive/${groupOfSurveyTypeId}`,
            archive
        );
    }

    public getSerachGroupOfSurveyTypes(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/groupOfSurveyTypes/search`,
            { params: queryParams }
        );
    }

    public getGroupOfSurveyType(
        groupOfSurveyTypeId: number
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.get<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}`
        );
    }

    public updateGroupOfSurveyTypeSurveyIndicators(
        groupOfSurveyTypeId: number,
        surveyIndicators: any
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.post<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/surveyIndicators`,
            surveyIndicators
        );
    }

    public addPointsGroupOfSurvey(
        groupOfSurveyTypeId: number,
        data: { sportId: number; physicalTypeId: number; points: number }
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.post<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/points`,
            data
        );
    }

    public addSurveyIndicatorForRasp(
        groupOfSurveyTypeId: number,
        data: { groups: number[]; items: number[] }
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.post<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/indicators`,
            data
        );
    }

    public addDisciplineForRasp(
        groupOfSurveyTypeId: number,
        data: { groups: number[]; item: number }
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.post<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/disciplines`,
            { groups: data.groups, items: [data.item] }
        );
    }

    public addPhysicalTypes(
        groupOfSurveyTypeId: number,
        groupId: number,
        items: number[]
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.post<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/group/${groupId}/physicalTypes`,
            items
        );
    }

    public deleteGroupOfSurveyTypeSport(
        groupOfSurveyTypeId: number,
        sportId: number
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.delete<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/sport/${sportId}`
        );
    }

    public deleteGroupOfSurveyTypePhysicalType(
        groupOfSurveyTypeId: number,
        physicalTypeId: number
    ): Observable<ServerResult<GroupOfSurveyType>> {
        return this.http.delete<ServerResult<GroupOfSurveyType>>(
            `${this.url}/groupOfSurveyType/${groupOfSurveyTypeId}/physicalType/${physicalTypeId}`
        );
    }

    // ---------------------------------------------------------------------------------------------

    public getCheckupProtocols(
        filter: SortFilter | null
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = this.AddQueryParamsBySortFilter(filter);
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/sportExamProtocols`,
            { params: queryParams }
        );
    }

    public updateCheckupProtocol(
        checkupProtocol: CommonList
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.put<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocol.id}`,
            checkupProtocol
        );
    }

    public addCheckupProtocol(
        checkupProtocol: CommonList
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/sportExamProtocol`,
            checkupProtocol
        );
    }

    public archiveCheckupProtocol(
        checkupProtocolId: number,
        archive: Archive
    ): Observable<ServerResult<CommonList>> {
        return this.http.post<ServerResult<CommonList>>(
            `${this.url}/sportExamProtocol/archive/${checkupProtocolId}`,
            archive
        );
    }

    public getSerachCheckupProtocols(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CommonList[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/sportExamProtocols/search`,
            { params: queryParams }
        );
    }

    public getCheckupProtocol(
        checkupProtocolId: number
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.get<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}`
        );
    }

    public addCheckupProtocolChapter(
        checkupProtocolId: number,
        chapter: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.put<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/chapter`,
            chapter
        );
    }

    public updateCheckupProtocolChapter(
        checkupProtocolId: number,
        chapter: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.post<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/chapter`,
            chapter
        );
    }

    public deleteCheckupProtocolChapter(
        checkupProtocolId: number,
        chapterId: number
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.delete<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/${chapterId}`
        );
    }

    public addCheckupProtocolEquipment(
        checkupProtocolId: number,
        equipment: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.put<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/equipment`,
            equipment
        );
    }

    public updateCheckupProtocolEquipment(
        checkupProtocolId: number,
        equipment: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.post<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/equipment`,
            equipment
        );
    }

    public deleteCheckupProtocolEquipment(
        checkupProtocolId: number,
        model: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.delete<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/${model.chapterId}/${model.id}`
        );
    }

    public addCheckupProtocolTypeOfExamination(
        checkupProtocolId: number,
        typeOfExamination: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.put<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/typeOfExamination`,
            typeOfExamination
        );
    }

    public updateCheckupProtocolTypeOfExamination(
        checkupProtocolId: number,
        typeOfExamination: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.post<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/typeOfExamination`,
            typeOfExamination
        );
    }

    public deleteCheckupProtocolTypeOfExamination(
        checkupProtocolId: number,
        model: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.delete<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/${model.chapterId}/${model.equipmentId}/${model.id}`
        );
    }

    public updateCheckupProtocolSurveyIndicators(
        checkupProtocolId: number,
        surveyIndicators: any
    ): Observable<ServerResult<CheckupProtocol>> {
        return this.http.post<ServerResult<CheckupProtocol>>(
            `${this.url}/sportExamProtocol/${checkupProtocolId}/surveyIndicators`,
            surveyIndicators
        );
    }

    // ---------------------------------------------------------------------------------------------

    public getAchiveReasons(): Observable<ServerResult<CommonList[]>> {
        return this.http.get<ServerResult<CommonList[]>>(
            `${this.url}/achiveReasons`
        );
    }

    // -------------------------------- ТЕСТИРУЕМЫЕ -------------------------------------------------------------

    public getPersons(
        filter: PersonFilter
    ): Observable<ServerResult<Person[]>> {
        let queryParams = this.AddQueryParamsByPersonFilter(filter);
        return this.http.get<ServerResult<Person[]>>(`${this.url}/clients`, {
            params: queryParams,
        });
    }

    public getSerachPersons(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<Person[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<Person[]>>(`${this.url}/clients`, {
            params: queryParams,
        });
    }

    public getPerson(
        personId: number,
        sportId: number
    ): Observable<ServerResult<Person>> {
        return this.http.get<ServerResult<Person>>(
            `${this.url}/client/${personId}/${sportId}`
        );
    }

    // -------------------------------- ПРОТОКОЛЫ   -------------------------------------------------------------

    public updateCheckupProtocolsSport(
        protocolId: number,
        payload: CheckupProtocolSport
    ): Observable<ServerResult<CheckupProtocolSport>> {
        return this.http.post<ServerResult<CheckupProtocolSport>>(
            `${this.url}/protocols/protocol/${protocolId}`,
            payload
        );
    }

    public addCheckupProtocolsSport(
        payload: CheckupProtocolSport
    ): Observable<ServerResult<CheckupProtocolSport>> {
        return this.http.put<ServerResult<CheckupProtocolSport>>(
            `${this.url}/protocols/protocol`,
            payload
        );
    }

    public getCheckupProtocolsSport(
        filter: ProtocolFilter
    ): Observable<ServerResult<CheckupProtocolSport[]>> {
        let queryParams =
            this.AddQueryParamsByCheckupProtocolSportFilter(filter);
        return this.http.get<ServerResult<CheckupProtocolSport[]>>(
            `${this.url}/protocols/list`,
            { params: queryParams }
        );
    }

    public getSerachCheckupProtocolsSport(
        searchString: string,
        pageIndex: number,
        pageSize: number
    ): Observable<ServerResult<CheckupProtocolSport[]>> {
        let queryParams = new HttpParams();
        if (searchString)
            queryParams = queryParams.append('searchString', searchString);
        if (pageIndex) queryParams = queryParams.append('pageIndex', pageIndex);
        if (pageSize) queryParams = queryParams.append('pageSize', pageSize);

        return this.http.get<ServerResult<CheckupProtocolSport[]>>(
            `${this.url}/protocols/list`,
            { params: queryParams }
        );
    }

    public getCheckupUniversalProtocol(
        id: number
    ): Observable<ServerResult<CheckupProtocolSport>> {
        return this.http.get<ServerResult<CheckupProtocolSport>>(
            `${this.url}/protocols/protocol/print/${id}`
        );
    }

    public getCheckupProtocolSport(
        id: number
    ): Observable<ServerResult<CheckupProtocolSport>> {
        return this.http.get<ServerResult<CheckupProtocolSport>>(
            `${this.url}/protocols/protocol/${id}`
        );
    }

    public getCheckupProtocolSportStructure(
        params: AddProtoModel
    ): Observable<ServerResult<CheckupProtocolSport>> {
        let queryParams = new HttpParams();

        if (params.sportId)
            queryParams = queryParams.append('sportId', params.sportId);

        if (params.sportsmanId)
            queryParams = queryParams.append('sportsmanId', params.sportsmanId);

        if (params.checkUpProtocolId)
            queryParams = queryParams.append(
                'checkUpProtocolId',
                params.checkUpProtocolId
            );

        if (params.groupDevelopQualitiesId)
            queryParams = queryParams.append(
                'groupDevelopQualitiesId',
                params.groupDevelopQualitiesId
            );

        if (params.physicalQualityId)
            queryParams = queryParams.append(
                'physicalQualityId',
                params.physicalQualityId
            );

        return this.http.get<ServerResult<CheckupProtocolSport>>(
            `${this.url}/protocols/protocol/structure`,
            { params: queryParams }
        );
    }

    public getCheckupProtocolSportReport(
        id: number
    ): Observable<ServerResult<CheckupProtocolSportReportBA>> {
        return this.http.get<ServerResult<CheckupProtocolSportReportBA>>(
            `${this.url}/protocolReport/${id}`
        );
    }
    public getCheckupProtocolSportReportByGuid(
        id: string
    ): Observable<ServerResult<CheckupProtocolSportReportBA>> {
        return this.http.get<ServerResult<CheckupProtocolSportReportBA>>(
            `${this.url}/protocolReport/byguid/${id}`
        );
    }

    public getCheckupProtocolSportReportDyn(
        ids: number[]
    ): Observable<ServerResult<CheckupProtocolSportReportBA[]>> {
        let queryString = '?';
        for (var index = 0; index < ids.length; index++) {
            queryString = queryString + `ProtocolIds[${index}]=${ids[index]}&`;
        }
        return this.http.get<ServerResult<CheckupProtocolSportReportBA[]>>(
            `${this.url}/protocolReport/dyn${queryString}`
        );
    }
    public getCheckupProtocolSportReportDynGuid(
        ids: string[]
    ): Observable<ServerResult<CheckupProtocolSportReportBA[]>> {
        const queryString =
            '?' + ids.map((id) => `ProtocolGuids=${id}&`).join('');
        return this.http.get<ServerResult<CheckupProtocolSportReportBA[]>>(
            `${this.url}/protocolReport/dyn/byguid${queryString}`
        );
    }

    public getCheckupProtocolSportReportGroup(
        ids: number[]
    ): Observable<ServerResult<CheckupProtocolSportReportBA[]>> {
        let queryString = '?';
        for (var index = 0; index < ids.length; index++) {
            queryString = queryString + `ProtocolIds[${index}]=${ids[index]}&`;
        }
        return this.http.get<ServerResult<CheckupProtocolSportReportBA[]>>(
            `${this.url}/protocolReport/group${queryString}`
        );
    }

    // -------------------------------- COMMON      -------------------------------------------------------------

    public getProtocolDates(
        personId: number,
        protocolId: number
    ): Observable<ServerResult<ProtocolDate[]>> {
        return this.http.get<ServerResult<ProtocolDate[]>>(
            `${this.url}/protocolDates/${personId}/${protocolId}`
        );
    }

    public getGroupDevelopQualitiesTree(): Observable<
        ServerResult<GroupDevelopQualitiesTree>
    > {
        return this.http.get<ServerResult<GroupDevelopQualitiesTree>>(
            `${this.url}/references/groupdevelopqualitiestree`
        );
    }

    public addProto(data): Observable<ServerResult<any>> {
        return this.http.put<ServerResult<any>>(
            `${this.url}/protocols/protocol`,
            data
        );
    }

    public getPhoto(url: string): Observable<ServerResult<any>> {
        return this.http.get<ServerResult<any>>(`${this.url}/${url}`);
    }

    // ------------------------------СПИСОК ЗНАЧЕНИЙ ПОКАЗАТЕЛЕЙ ОБСЛЕДОВАНИЯ-------------------------------------------

    public getControlExerciseValues(
        controlExerciseId: number
    ): Observable<ServerResult<ControlExerciseValueDto[]>> {
        return this.http.get<ServerResult<ControlExerciseValueDto[]>>(
            `${this.url}/controlexercisevalue/list?ControlExerciseId=${controlExerciseId}`
        );
    }

    /**
     * Удаление элемента по ID.
     * Эндпоинт: DELETE /api/controlexercisevalue/{id}
     * @param id Идентификатор элемента
     */
    public deleteControlExerciseValue(
        id: number
    ): Observable<ServerResult<ControlExerciseValueDto>> {
        return this.http.delete<ServerResult<ControlExerciseValueDto>>(
            `${this.url}/controlexercisevalue/${id}`
        );
    }

    /**
     * Создание нового элемента.
     * Эндпоинт: POST /api/controlexercisevalue
     * @param newValue Данные для создания нового элемента
     */
    public createControlExerciseValue(
        newValue: CreateControlExerciseValueDto
    ): Observable<ServerResult<ControlExerciseValueDto>> {
        return this.http.post<ServerResult<ControlExerciseValueDto>>(
            `${this.url}/controlexercisevalue`,
            newValue
        );
    }

    /**
     * Обновление существующего элемента.
     * Эндпоинт: PUT /api/controlexercisevalue
     * @param updatedValue Данные для обновления элемента
     */
    public updateControlExerciseValue(
        updatedValue: UpdateControlExerciseValueDto
    ): Observable<ServerResult<ControlExerciseValueDto>> {
        return this.http.put<ServerResult<ControlExerciseValueDto>>(
            `${this.url}/controlexercisevalue`,
            updatedValue
        );
    }
}
